import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import { createAdminReview } from '../../apollo'

const CREATE_ADMIN_REVIEW = gql`
  ${createAdminReview}
`

const RATINGS = [1, 2, 3, 4, 5]
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Admin-recorded review. There is no order behind it, so the reviewer's name and
// email are typed rather than taken from an account.
function AddRatingModal(props) {
  const { t, open, onClose, restaurantId, onCreated } = props
  const theme = useTheme()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [description, setDescription] = useState('')
  const [rating, setRating] = useState(5)
  const [formError, setFormError] = useState('')

  const [mutate, { loading }] = useMutation(CREATE_ADMIN_REVIEW, {
    onCompleted: () => {
      resetForm()
      if (typeof onCreated === 'function') onCreated()
      onClose()
    },
    onError: error =>
      setFormError(error?.message || 'Could not add the rating.')
  })

  const resetForm = () => {
    setName('')
    setEmail('')
    setDescription('')
    setRating(5)
    setFormError('')
  }

  const onCancel = () => {
    resetForm()
    onClose()
  }

  const onSubmit = () => {
    if (loading) return
    const trimmedName = name.trim()
    if (!trimmedName) {
      setFormError(t('ReviewerNameRequired'))
      return
    }
    // Email is optional, but a typo should not be stored silently.
    const trimmedEmail = email.trim()
    if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
      setFormError(t('InvalidEmail'))
      return
    }
    if (!restaurantId) {
      setFormError(t('NoRestaurantSelected'))
      return
    }
    setFormError('')
    mutate({
      variables: {
        reviewInput: {
          restaurant: restaurantId,
          rating: Number(rating),
          description: description.trim(),
          reviewerName: trimmedName,
          reviewerEmail: trimmedEmail
        }
      }
    })
  }

  // Inputs set color: text.primary themselves, which this theme defines as #fff,
  // so typed values render white on white unless overridden here.
  const fieldSx = {
    mt: 2,
    '& .MuiInputBase-input': { color: theme.palette.text.secondary },
    '& .MuiSelect-select': { color: theme.palette.text.secondary },
    '& .MuiInputLabel-root': { color: theme.palette.text.disabled },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    }
  }

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: theme.palette.text.secondary
        }}>
        {t('AddRating')}
      </DialogTitle>
      <DialogContent sx={{ color: theme.palette.text.secondary }}>
        <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
          {t('AddRatingHint')}
        </Typography>

        <TextField
          fullWidth
          required
          size="small"
          label={t('Name')}
          value={name}
          onChange={event => setName(event.target.value)}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('Email')}
          value={email}
          onChange={event => setEmail(event.target.value)}
          sx={fieldSx}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t('Ratings')}
          value={rating}
          onChange={event => setRating(event.target.value)}
          sx={fieldSx}>
          {RATINGS.map(value => (
            <MenuItem
              key={value}
              value={value}
              sx={{ color: theme.palette.text.secondary }}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label={t('Review')}
          value={description}
          onChange={event => setDescription(event.target.value)}
          sx={fieldSx}
        />

        {formError && (
          <Box mt={2}>
            <Alert severity="error">{formError}</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={loading}
          onClick={onSubmit}
          sx={{
            textTransform: 'none',
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.main3 }
          }}>
          {loading ? t('Saving') : t('AddRating')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withTranslation()(AddRatingModal)
